// Organization-scoped application addresses, built in one place so local IDs
// never cross an organization boundary during navigation.

/**
 * Every organization the viewer is a member of.
 *
 * Named for what the page lists. The module around it still says "workspace"
 * in its symbols, but a URL is the one name users read, bookmark and paste to
 * each other, so it says the word the product says.
 */
export const DIRECTORY_PATH = "/organizations";

/** `/w` rather than `/orgs`, so an old bookmark cannot resolve to a tenant. */
export const WORKSPACE_ROOT = "/w";

export function workspacePath(slug: string, sub = ""): string {
  const root = `${WORKSPACE_ROOT}/${encodeURIComponent(slug)}`;
  return sub === "" ? root : `${root}/${sub}`;
}

/**
 * Read the workspace out of a pathname.
 *
 * The app shell renders ABOVE the workspace route, so it cannot use route
 * params — and giving it its own "current workspace" state is the bug this
 * whole design removes. It reads the same URL the router does instead.
 */
export function workspaceSlugFromPath(pathname: string): string {
  const parts = pathname.split("/").filter((part) => part !== "");
  if (parts.length < 2 || `/${parts[0]}` !== WORKSPACE_ROOT) return "";
  return decodeURIComponent(parts[1]);
}

export interface WorkspaceLinks {
  home: string;
  /** The accepted roster. */
  characters: string;
  /** Archived characters: a place of their own, not a filter on the listing. */
  characterArchive: string;
  /** One accepted or archived character's Identity section. */
  character: (characterID: string) => string;
  characterLibrary: (characterID: string) => string;
  characterActivity: (characterID: string) => string;
  /** Everything before acceptance: open drafts, authoring and review. */
  drafts: string;
  newDraft: string;
  draft: (draftID: string) => string;
  draftReview: (draftID: string) => string;
  job: (jobID: string) => string;
  audit: string;
  /** Existing organization membership, domains, roles, and removals. */
  team: string;
}

export function createWorkspaceLinks(slug: string): WorkspaceLinks {
  const id = encodeURIComponent;
  const character = (characterID: string) =>
    workspacePath(slug, `characters/${id(characterID)}`);
  const draft = (draftID: string) =>
    workspacePath(slug, `drafts/${id(draftID)}`);
  return {
    home: workspacePath(slug, "characters"),
    characters: workspacePath(slug, "characters"),
    characterArchive: workspacePath(slug, "characters/archive"),
    character,
    characterLibrary: (characterID) => `${character(characterID)}/library`,
    characterActivity: (characterID) => `${character(characterID)}/activity`,
    drafts: workspacePath(slug, "drafts"),
    newDraft: workspacePath(slug, "drafts/new"),
    draft,
    draftReview: (draftID) => `${draft(draftID)}/review`,
    job: (jobID) => workspacePath(slug, `jobs/${id(jobID)}`),
    audit: workspacePath(slug, "settings/audit"),
    team: workspacePath(slug, "settings/team"),
  };
}
